import { LineItemEditorData } from '@/ui/components/addEditProposalLineItem/defaultValues';
import { TFormState } from '@/ui/components/addEditProposalLineItem/formState';
import {
  derivePriceInput,
  diffLinkedMaterials,
} from '@/ui/components/addEditProposalLineItem/lineItemSubmit';
import {
  useOptimisticRemoveEstimateLineItem,
  useOptimisticUpdateEstimateLineItem,
  useOptimisticUpdateEstimateLineItemMaterials,
} from '../api/mutations';
import { EstimateLineItem } from '../types';

type UseEstimateLineItemEditorArgs = {
  projectId: string;
  estimateId: string;
  lineItem: EstimateLineItem;
  onRemoveSuccess: () => void;
};

/**
 * Data + mutation wiring for editing a single Estimate Line Item, shaped to
 * match the contract `AddEditProposalLineItemContent` consumes. The Estimate
 * Builder already holds the line item (with its materials and pricing) in the
 * GetProjectEstimates cache, so this reads from the passed `lineItem` rather
 * than a dedicated query, and the mutations hydrate that cache on success.
 */
const useEstimateLineItemEditor = (args: UseEstimateLineItemEditorArgs) => {
  const { projectId, estimateId, lineItem, onRemoveSuccess } = args;

  const updateEstimateLineItem = useOptimisticUpdateEstimateLineItem({
    projectId,
    estimateId,
  });
  const updateEstimateLineItemMaterials =
    useOptimisticUpdateEstimateLineItemMaterials({ projectId, estimateId });
  const removeEstimateLineItem = useOptimisticRemoveEstimateLineItem({
    projectId,
    estimateId,
  });

  // Estimate line items carry no `selection` or `thumbnail`; the editor hides
  // both. Everything else maps straight onto the shared editor's data shape.
  const lineItemData: LineItemEditorData = {
    internalReference: lineItem.internalReference,
    description: lineItem.description,
    quantity: lineItem.quantity,
    price: lineItem.price,
    materials: lineItem.materials,
    lineItem: lineItem.lineItem,
  };

  const onSubmit = async (state: TFormState) => {
    const updateOriginal =
      state.canUpdateOriginalLineItem && state.updateOriginalLineItem;

    const { changes, unchanged } = diffLinkedMaterials(
      lineItem.materials,
      state.materials
    );

    // Skip the materials write when the linked-material set is unchanged since
    // the last save — unless we're also pushing it onto the original catalogue
    // Item, where the write is what propagates the materials.
    if (updateOriginal || !unchanged) {
      await updateEstimateLineItemMaterials.mutateAsync({
        estimateLineItemId: lineItem.id,
        materials: changes,
        updateLineItem: updateOriginal,
      });
    }

    await updateEstimateLineItem.mutateAsync({
      id: lineItem.id,
      internalReference: state.reference,
      description: state.description,
      quantity: state.quantity,
      price: {
        ...derivePriceInput(state),
        taxRateId: state.taxRateId,
        cisApplies: state.cis === 'yes',
        discount: state.hasDiscount
          ? {
              percentage: state.discountPercentage || 0,
              description: state.discountDescription,
            }
          : null,
      },
      updateLineItem: updateOriginal,
    });
  };

  const onDelete = async () => {
    await removeEstimateLineItem.mutateAsync({ id: lineItem.id });
    onRemoveSuccess();
  };

  // Estimates have no line item thumbnails; the editor hides the field.
  const onThumbnailDelete = async () => {};

  return {
    lineItemData,
    isSubmitting:
      updateEstimateLineItem.isPending ||
      updateEstimateLineItemMaterials.isPending,
    onSubmit,
    onThumbnailDelete,
    onDelete,
  };
};

export default useEstimateLineItemEditor;
